import { useEffect, useState } from 'react';
import { Section } from '@vnetwork/ui/layout';
import { Button, Textarea, Skeleton } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useServiceWeakPassword, useUpdateServiceWeakPassword, ADVANCED_TYPE_WEAK_PASSWORD } from '@/api/waap';
import { AdvancedStrategyPanel } from '@/pages/services/waap/aiwaf/advanced-protection/AdvancedStrategyPanel';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WeakPasswordPanel.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * Weak Password Validation tab. Two parts:
 *   1. Weak Password Validation Rules — a waf_advanced_strategy list (awaiting the
 *      vendor's rule type + create payload, so the Add form isn't wired yet).
 *   2. Weak Password List — the account weak-password dictionary (GET /weak_password):
 *      "Show List" reveals an editable textarea (newline-separated) + Save.
 */
export function WeakPasswordPanel({ serviceId, base }: { serviceId: string; base: string }) {
  const { show } = useToast();
  const [showList, setShowList] = useState(false);
  const [content, setContent] = useState('');

  const { data, isLoading, isError } = useServiceWeakPassword(serviceId, showList);
  useEffect(() => {
    if (data) setContent(data.content ?? '');
  }, [data]);

  const update = useUpdateServiceWeakPassword(serviceId, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'weak_password_list', 'edit');
      show({ tone: 'success', title: 'Weak password list saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  return (
    <>
      <AdvancedStrategyPanel
        serviceId={serviceId}
        base={base}
        type={ADVANCED_TYPE_WEAK_PASSWORD}
        title="Weak Password Validation Rule"
        description="Weak password validation detects and handles weak passwords in login requests based on the configured policy."
        addLabel="Add Weak Password Validation Rule"
        formSegment="weak-password"
        noun="Weak Password rule"
      />

      <Section
        title="Weak Password List"
        description="This is the common weak-password list. Edit it to fit your business needs — one password per line."
        actions={
          !showList && (
            <Button variant="secondary" onClick={() => setShowList(true)}>
              Show List
            </Button>
          )
        }
      >
        {!showList ? null : isLoading ? (
          <Skeleton className={styles.skel} />
        ) : isError ? (
          muted('Could not load the weak-password list. Please retry shortly.')
        ) : (
          <div className={styles.content}>
            <Textarea
              value={content}
              rows={14}
              className={styles.mono}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setContent(data?.content ?? '');
                  setShowList(false);
                }}
              >
                Cancel
              </Button>
              <Button disabled={update.isPending} onClick={() => update.mutate(content)}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Section>
    </>
  );
}
